import {Badge, Box, Button, Stack, TableCell, TableRow, Tooltip, Typography, IconButton} from "@mui/material";
import {getConversationIcon, getChatIcon} from "../../../../utils/functions";
import moment from "moment";
import React, {useMemo, useState} from "react";
import {useSetAtom} from "jotai";
import {SelectedConversationAtom} from "../atoms";
import {TOKEN} from "../../../../context/SignInContext";
import {IconChecks, IconEye, IconEyeOff, IconMessage2} from "@tabler/icons-react";
import {green, grey} from "@mui/material/colors";
import CompactAudioPlayer from "./CompactAudioPlayer"
import {IconRobot, IconCopy, IconCheck} from "@tabler/icons-react";


export const ConversationTableRow = ({conversation, project}) => {
    const setSelectedConversation = useSetAtom(SelectedConversationAtom);


    const getAudioUrl = () => {
        const base = project?.configuration?.conversation_logger_api || "https://api.voicebot.openbrain.io";
        const path = conversation?.audio; // assume something like "/usr/src/asterisk/recordings/397/4367.wav"
        const url = new URL("/api/file/audio", base);
        url.searchParams.append("path", path);
        url.searchParams.append("jwt", localStorage.getItem(TOKEN));

        return url.toString();
    };

    const [copied, setCopied] = useState(false);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).catch(() => {
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const contact = useMemo(() => {
        return project?.phonebook?.[conversation?.sender] || null;
    }, [project?.phonebook?.[conversation?.sender], conversation?.sender])
    const selectedTags = project?.settings?.tags?.filter(i => conversation?.tags?.includes(i?.id));
    const successTag = project?.settings?.tags?.find(tag => tag?.id === project?.configuration?.success_tag) || null;


    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        // <Tooltip title={"Click to open conversation"}>
        <TableRow key={conversation?.id} hover id={`conversation-${conversation?.id}`} sx={{cursor: "pointer"}}
                  onClick={() => setSelectedConversation(conversation)}>
            <TableCell padding={"checkbox"} sx={{py: 1, px: 1, width: 30, maxWidth: 30}}>
                <Stack direction={"row"} gap={1} alignItems={"center"} sx={{minWidth: 0}}>
                    {type === "CHAT_BOT" ? getChatIcon(conversation?.status) :
                        conversation?.resolved && conversation?.status !== "SUCCESS" ?
                            <IconChecks size={18} color={grey[600]}/> :
                            getConversationIcon(conversation?.status)
                    }
                    {/*{type === "CHAT_BOT" && <IconRobot size={16} color={grey[600]}/>}*/}
                    <Typography fontSize={13} component={"span"} noWrap
                                sx={{overflow: "hidden", textOverflow: "ellipsis"}}>
                        {conversation?.id}
                    </Typography>
                </Stack>
            </TableCell>

            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 1, width: "220px", minWidth: "220px"}}>
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    width: "100%",
                    '&:hover .copy-btn': {opacity: 1}
                }}>
                    <Typography sx={{cursor: "pointer"}}
                                fontSize={13} fontWeight={500} noWrap>
                        {String(contact || conversation?.sender || '').length > 20
                            ? `${String(contact || conversation?.sender).slice(0, 20)}...`
                            : (contact || conversation?.sender)}
                    </Typography>
                    {type === "CHAT_BOT" && (
                        <IconButton
                            size="small"
                            className="copy-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(contact || conversation?.sender);
                            }}
                            sx={{opacity: 0, transition: 'opacity 0.15s', p: 0.25, ml: 0.5, flexShrink: 0}}
                        >
                            {copied ? <IconCheck size={13} color="green"/> : <IconCopy size={13}/>}
                        </IconButton>
                    )}
                </Box>
            </TableCell>
            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 1, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                <Typography noWrap
                            fontSize={13}>{moment(conversation?.dateCreated).format("DD MMM YYYY HH:mm")}</Typography>
            </TableCell>
            <TableCell
                padding={"checkbox"}
                sx={{
                    py: 1,
                    px: 1,
                    fontSize: 13,
                    display: {
                        xs: "none", md: "table-cell",
                        width: type === "CHAT_BOT" ? 30 : 60,
                        maxWidth: type === "CHAT_BOT" ? 30 : 60
                    },
                    textTransform: "capitalize"
                }}>
                {conversation?.status?.toLowerCase()?.replaceAll("_", " ")}
                {conversation?.vdn || conversation?.transferNumber ? ` (${conversation?.vdn || conversation?.transferNumber})` : ""}
            </TableCell>

            <TableCell padding={"checkbox"} sx={{py: 1, px: 1, overflow: "hidden", width: "80px", maxWidth: "80px"}}>
                {(() => {
                    const displayText = conversation.status === "SUCCESS" && successTag?.title
                        ? successTag?.title
                        : selectedTags?.length > 0
                            ? selectedTags?.filter(i => !!i?.title)?.map(i => i?.title)?.join(", ")
                            : "Just started...";

                    return (
                        <Tooltip title={displayText}>
                            <Typography noWrap fontSize={11} fontFamily="Roboto"
                                        sx={{overflow: "hidden", textOverflow: "ellipsis"}}>
                                {displayText}
                            </Typography>
                        </Tooltip>
                    );
                })()}
            </TableCell>
            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 0.5, width: 30, maxWidth: 30, display: {xs: "none", md: "table-cell"}}}>
                <Stack direction={"row"} gap={0.5} alignItems={"center"} justifyContent={"center"}>
                    <Badge invisible={(conversation?.comments?.length || 0) === 0} color={"error"} variant="dot">
                        <IconMessage2 size={15} color={grey[600]}/>
                    </Badge>
                    <Tooltip title={conversation?.monitoredBy || "Not supervised"}>
                        {conversation?.monitoredBy ? <IconEye color={grey[600]} size={15}/> :
                            <IconEyeOff color={grey[600]} size={15}/>}
                    </Tooltip>
                </Stack>
            </TableCell>

            {type === "VOICE_BOT" ? <TableCell padding={"checkbox"} sx={{width: "auto"}}>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"flex-end"}
                           sx={{width: "auto", py: 0.5, fontSize: 13}}>
                        <Box sx={{display: {xs: "none", md: "flex"}, fontSize: "inherit", pr: 1}}>
                            <CompactAudioPlayer src={getAudioUrl()}/>
                        </Box>
                    </Stack>
                </TableCell>
                : null}
        </TableRow>
        // </Tooltip>
    )
}